import React, { useState, useEffect, useRef } from 'react';
import { db } from '../firebaseConfig';
import { doc, onSnapshot, updateDoc } from 'firebase/firestore';
import { jsPDF } from 'jspdf';

// ── Result chart helpers ──────────────────────────────────────────────────────

function YesNoChart({ answers }) {
  const yes = answers.filter(a => a.answer === 'Yes').length;
  const no  = answers.filter(a => a.answer === 'No').length;
  const total = answers.length || 1;
  return (
    <div className="result-chart">
      <div className="result-bar-row">
        <span className="result-bar-label">Yes</span>
        <div className="result-bar-track">
          <div className="result-bar-fill result-bar-fill--yes" style={{ width: `${(yes / total) * 100}%` }} />
        </div>
        <span className="result-bar-count">{yes}</span>
      </div>
      <div className="result-bar-row">
        <span className="result-bar-label">No</span>
        <div className="result-bar-track">
          <div className="result-bar-fill result-bar-fill--no" style={{ width: `${(no / total) * 100}%` }} />
        </div>
        <span className="result-bar-count">{no}</span>
      </div>
      <div className="result-chart__footer">{answers.length} response{answers.length !== 1 ? 's' : ''}</div>
    </div>
  );
}

function ScaleChart({ answers }) {
  const nums = answers.map(a => Number(a.answer)).filter(n => !isNaN(n));
  if (nums.length === 0) return <p className="muted">No responses.</p>;
  const avg = (nums.reduce((s, n) => s + n, 0) / nums.length).toFixed(1);
  const min = Math.min(...nums);
  const max = Math.max(...nums);
  // bucket into 10 bins
  const bins = Array(10).fill(0);
  nums.forEach(n => { bins[Math.min(9, Math.floor((n - 1) / 10))]++; });
  const binMax = Math.max(...bins, 1);
  return (
    <div className="result-chart">
      <div className="scale-stat-row">
        <div className="scale-stat"><div className="scale-stat__val">{avg}</div><div className="scale-stat__lbl">Average</div></div>
        <div className="scale-stat"><div className="scale-stat__val">{min}</div><div className="scale-stat__lbl">Min</div></div>
        <div className="scale-stat"><div className="scale-stat__val">{max}</div><div className="scale-stat__lbl">Max</div></div>
        <div className="scale-stat"><div className="scale-stat__val">{nums.length}</div><div className="scale-stat__lbl">Responses</div></div>
      </div>
      <div className="scale-histogram">
        {bins.map((count, i) => (
          <div key={i} className="scale-histogram__col">
            <div className="scale-histogram__bar" style={{ height: `${(count / binMax) * 100}%` }} />
            <div className="scale-histogram__lbl">{i * 10 + 1}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

function McqChart({ answers, options }) {
  const counts = {};
  options.forEach(o => { counts[o] = 0; });
  answers.forEach(a => {
    const parts = String(a.answer).split(', ');
    parts.forEach(p => { if (counts[p] !== undefined) counts[p]++; });
  });
  const total = answers.length || 1;
  const colors = ['#6C3FC5', '#22c55e', '#f59e0b', '#ef4444', '#3b82f6', '#ec4899'];
  return (
    <div className="result-chart">
      {options.map((opt, i) => (
        <div key={i} className="result-bar-row">
          <span className="result-bar-label">{opt}</span>
          <div className="result-bar-track">
            <div className="result-bar-fill" style={{ width: `${(counts[opt] / total) * 100}%`, background: colors[i % colors.length] }} />
          </div>
          <span className="result-bar-count">{counts[opt]}</span>
        </div>
      ))}
      <div className="result-chart__footer">{answers.length} response{answers.length !== 1 ? 's' : ''}</div>
    </div>
  );
}

function EmojiChart({ answers }) {
  const counts = {};
  answers.forEach(a => {
    const e = String(a.answer);
    counts[e] = (counts[e] || 0) + 1;
  });
  const sorted = Object.entries(counts).sort((a, b) => b[1] - a[1]);
  const total = answers.length || 1;
  return (
    <div className="result-chart">
      <div className="emoji-result-grid">
        {sorted.map(([emoji, count]) => (
          <div key={emoji} className="emoji-result-item">
            <span className="emoji-result-item__emoji">{emoji}</span>
            <span className="emoji-result-item__count">{count}</span>
            <div className="emoji-result-item__bar">
              <div style={{ height: `${(count / total) * 100}%`, background: 'var(--brand)', borderRadius: 4 }} />
            </div>
          </div>
        ))}
      </div>
      <div className="result-chart__footer">{answers.length} response{answers.length !== 1 ? 's' : ''}</div>
    </div>
  );
}

function ParagraphChart({ answers }) {
  // Group by exact match first, then show ungrouped
  const counts = {};
  answers.forEach(a => {
    const key = String(a.answer).trim().toLowerCase();
    if (!counts[key]) counts[key] = { display: String(a.answer).trim(), count: 0 };
    counts[key].count++;
  });
  const groups = Object.values(counts).sort((a, b) => b.count - a.count);
  return (
    <div className="result-chart">
      {groups.map((g, i) => (
        <div key={i} className="para-result-item">
          {g.count > 1 && <span className="para-result-item__badge">×{g.count}</span>}
          <span className="para-result-item__text">{g.display}</span>
        </div>
      ))}
      <div className="result-chart__footer">{answers.length} response{answers.length !== 1 ? 's' : ''}</div>
    </div>
  );
}

function ResultsPanel({ question, answers, onNext, isLast }) {
  const renderChart = () => {
    switch (question.type) {
      case 'yes_no':      return <YesNoChart answers={answers} />;
      case 'scale':       return <ScaleChart answers={answers} />;
      case 'single_mcq':
      case 'multi_mcq':   return <McqChart answers={answers} options={question.options || []} />;
      case 'emoji_selector': return <EmojiChart answers={answers} />;
      case 'paragraph':
      case 'photo':       return <ParagraphChart answers={answers} />;
      default:            return <ParagraphChart answers={answers} />;
    }
  };
  return (
    <div className="results-overlay">
      <div className="results-overlay__header">
        <span className="question-item__type">{question.type}</span>
        <span style={{ fontWeight: 700, fontSize: '1.1rem', flex: 1, marginLeft: 12 }}>{question.prompt}</span>
      </div>
      {renderChart()}
      <button className="btn btn--primary btn--full btn--lg" style={{ marginTop: 20 }} onClick={onNext}>
        {isLast ? 'Finish Game' : 'Next Question →'}
      </button>
    </div>
  );
}

// ── End-of-game summary ───────────────────────────────────────────────────────

const TOPIC_KEYWORDS = {
  'Feelings & Mood':   ['feel', 'mood', 'emotion', 'happy', 'sad', 'stress', 'enjoy', 'like', 'love', 'hate', 'prefer'],
  'Work & Productivity': ['work', 'task', 'project', 'deadline', 'meeting', 'team', 'productivity', 'focus', 'job', 'role'],
  'Food & Drink':      ['food', 'eat', 'drink', 'meal', 'lunch', 'dinner', 'breakfast', 'coffee', 'snack', 'hungry'],
  'Learning & Skills': ['learn', 'skill', 'know', 'understand', 'study', 'course', 'training', 'experience', 'knowledge'],
  'Opinion & Feedback':['think', 'opinion', 'feedback', 'suggest', 'improve', 'rate', 'score', 'review', 'best', 'worst'],
};

function inferTopic(prompt) {
  const lower = prompt.toLowerCase();
  for (const [topic, keywords] of Object.entries(TOPIC_KEYWORDS)) {
    if (keywords.some(k => lower.includes(k))) return topic;
  }
  return 'General';
}

function EndSummary({ gameState }) {
  const groups = {};
  gameState.questions.forEach((q, idx) => {
    const topic = inferTopic(q.prompt);
    if (!groups[topic]) groups[topic] = [];
    groups[topic].push({ q, idx, answers: gameState.responses?.[idx] || [] });
  });

  return (
    <div>
      {Object.entries(groups).map(([topic, items]) => (
        <div key={topic} className="summary-group">
          <div className="summary-group__topic">{topic}</div>
          {items.map(({ q, idx, answers }) => (
            <div key={idx} className="card" style={{ marginBottom: 12 }}>
              <div className="flex items-center" style={{ gap: 10, marginBottom: 10 }}>
                <div className="question-item__num">{idx + 1}</div>
                <div>
                  <div className="question-item__type">{q.type}</div>
                  <div style={{ fontWeight: 700 }}>{q.prompt}</div>
                </div>
                <span className="muted" style={{ marginLeft: 'auto', fontSize: '0.85rem' }}>{answers.length} responses</span>
              </div>
              {answers.length > 0 && (() => {
                switch (q.type) {
                  case 'yes_no':         return <YesNoChart answers={answers} />;
                  case 'scale':          return <ScaleChart answers={answers} />;
                  case 'single_mcq':
                  case 'multi_mcq':      return <McqChart answers={answers} options={q.options || []} />;
                  case 'emoji_selector': return <EmojiChart answers={answers} />;
                  default:               return <ParagraphChart answers={answers} />;
                }
              })()}
              {answers.length === 0 && <p className="muted" style={{ fontSize: '0.85rem' }}>No responses.</p>}
            </div>
          ))}
        </div>
      ))}
    </div>
  );
}

// ── Main component ────────────────────────────────────────────────────────────

export default function HostScreen({ gamePin, onBack }) {
  const [pinInput, setPinInput]   = useState(gamePin || '');
  const [activePin, setActivePin] = useState(gamePin || '');
  const [gameState, setGameState] = useState(null);
  const [copied, setCopied]       = useState(false);
  const [showResults, setShowResults] = useState(false);
  const autoAdvancedRef = useRef(false);

  useEffect(() => {
    if (!activePin) return;
    const unsub = onSnapshot(doc(db, 'games', activePin), (snap) => {
      if (snap.exists()) setGameState(snap.data());
    });
    return () => unsub();
  }, [activePin]);

  // Reset auto-advance flag when question changes
  useEffect(() => {
    autoAdvancedRef.current = false;
    setShowResults(false);
  }, [gameState?.currentQuestionIndex]);

  // Auto-show results when everyone has answered
  useEffect(() => {
    if (!gameState || gameState.status !== 'question') return;
    if (autoAdvancedRef.current) return;
    const currentAnswers = gameState.responses?.[gameState.currentQuestionIndex] || [];
    if (gameState.paxCount > 0 && currentAnswers.length >= gameState.paxCount) {
      autoAdvancedRef.current = true;
      setShowResults(true);
    }
  }, [gameState]);

  const connect = () => {
    const clean = pinInput.trim().toUpperCase();
    if (clean.length < 6) return alert('Enter a valid 6-character PIN.');
    setActivePin(clean);
  };

  const advanceGame = async () => {
    setShowResults(false);
    const nextIndex = gameState.currentQuestionIndex + 1;
    await updateDoc(doc(db, 'games', activePin), {
      currentQuestionIndex: nextIndex,
      status: nextIndex >= gameState.questions.length ? 'ended' : 'question',
    });
  };

  const exportPDF = () => {
    const pdf = new jsPDF({ unit: 'mm', format: 'a4' });
    const pageW = pdf.internal.pageSize.getWidth();
    const margin = 16;
    const contentW = pageW - margin * 2;
    let y = margin;

    const checkPage = (needed = 10) => {
      if (y + needed > 280) { pdf.addPage(); y = margin; }
    };

    pdf.setFillColor(108, 63, 197);
    pdf.rect(0, 0, pageW, 28, 'F');
    pdf.setTextColor(255, 255, 255);
    pdf.setFontSize(18);
    pdf.setFont('helvetica', 'bold');
    pdf.text('QuizSync — Session Report', margin, 18);
    pdf.setFontSize(10);
    pdf.setFont('helvetica', 'normal');
    pdf.text(`PIN: ${activePin}  |  ${new Date().toLocaleString()}`, margin, 24);
    y = 38;

    pdf.setTextColor(30, 30, 30);
    pdf.setFontSize(11);
    pdf.setFont('helvetica', 'bold');
    pdf.text('Session Summary', margin, y); y += 7;
    pdf.setFont('helvetica', 'normal');
    pdf.setFontSize(10);
    pdf.text(`Total Questions: ${gameState.questions.length}`, margin, y); y += 6;
    pdf.text(`Total Participants: ${gameState.paxCount}`, margin, y); y += 6;
    const totalResponses = Object.values(gameState.responses || {}).reduce((s, a) => s + a.length, 0);
    pdf.text(`Total Responses Captured: ${totalResponses}`, margin, y); y += 12;

    gameState.questions.forEach((q, idx) => {
      checkPage(20);
      pdf.setFillColor(237, 232, 251);
      pdf.roundedRect(margin, y, contentW, 10, 2, 2, 'F');
      pdf.setTextColor(78, 45, 154);
      pdf.setFontSize(10);
      pdf.setFont('helvetica', 'bold');
      pdf.text(`Q${idx + 1}  [${q.type}]  ${q.prompt}`, margin + 3, y + 7);
      y += 14;

      const answers = gameState.responses?.[idx] || [];
      if (answers.length === 0) {
        pdf.setTextColor(150, 150, 150);
        pdf.setFont('helvetica', 'italic');
        pdf.setFontSize(9);
        pdf.text('No responses recorded.', margin + 4, y); y += 8;
      } else {
        answers.forEach((r, i) => {
          checkPage(8);
          pdf.setTextColor(60, 60, 60);
          pdf.setFont('helvetica', 'normal');
          pdf.setFontSize(9);
          const ansText = String(r.answer).substring(0, 90);
          pdf.text(`${i + 1}.  User ${r.userId.substring(5, 10)}   →   ${ansText}`, margin + 4, y);
          y += 7;
        });
      }
      y += 6;
    });

    pdf.setTextColor(150, 150, 150);
    pdf.setFontSize(8);
    pdf.text('Generated by QuizSync', margin, 290);
    pdf.save(`quizsync_report_${activePin}.pdf`);
  };

  const copyPlayerLink = () => {
    const url = `${window.location.origin}${window.location.pathname}?mode=player`;
    navigator.clipboard.writeText(url);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  if (!activePin) {
    return (
      <div className="page-center">
        <div className="container" style={{ maxWidth: 440 }}>
          <button className="btn btn--ghost btn--sm" style={{ marginBottom: 24 }} onClick={onBack}>← Back</button>
          <div className="card">
            <div className="section-title">Host a Game</div>
            <p className="muted" style={{ marginBottom: 20 }}>Enter the PIN of a game you already created.</p>
            <label className="label">Game PIN</label>
            <input
              className="input input--pin"
              type="text"
              maxLength={6}
              placeholder="XXXXXX"
              value={pinInput}
              onChange={e => setPinInput(e.target.value.toUpperCase())}
              onKeyDown={e => e.key === 'Enter' && connect()}
              autoFocus
            />
            <button className="btn btn--primary btn--full btn--lg" style={{ marginTop: 16 }} onClick={connect}>
              Connect to Game
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (!gameState) {
    return (
      <div className="page-center">
        <div className="container" style={{ maxWidth: 440, textAlign: 'center' }}>
          <p className="muted">Connecting to game room <strong>{activePin}</strong>...</p>
        </div>
      </div>
    );
  }

  const currentQ       = gameState.questions[gameState.currentQuestionIndex];
  const currentAnswers = gameState.responses?.[gameState.currentQuestionIndex] || [];
  const progress       = gameState.currentQuestionIndex >= 0
    ? ((gameState.currentQuestionIndex + 1) / gameState.questions.length) * 100
    : 0;

  const statusBadge = { lobby: 'badge--lobby', question: 'badge--live', ended: 'badge--ended' }[gameState.status];
  const statusLabel = { lobby: 'Lobby', question: 'Live', ended: 'Ended' }[gameState.status];

  return (
    <div style={{ minHeight: '100vh', background: 'var(--bg)', padding: '0 0 40px' }}>
      {/* Top bar */}
      <div style={{ background: 'linear-gradient(135deg, var(--brand) 0%, #9b59f5 100%)', padding: '20px 24px' }}>
        <div className="container container--wide">
          <div className="flex items-center justify-between flex-wrap" style={{ gap: 12 }}>
            <div>
              <div className="pin-label">Game PIN</div>
              <div className="pin-display">{activePin}</div>
            </div>
            <div className="flex flex-col items-center" style={{ gap: 8 }}>
              <div className="stat-pill">
                <span>👥</span>
                <span>{gameState.paxCount} participants</span>
              </div>
              <span className={`badge ${statusBadge}`}>{statusLabel}</span>
            </div>
            <div className="flex" style={{ gap: 8, flexWrap: 'wrap' }}>
              <button className="btn btn--sm" style={{ background: 'rgba(255,255,255,0.2)', color: 'white', border: '1px solid rgba(255,255,255,0.3)' }} onClick={copyPlayerLink}>
                {copied ? 'Copied!' : 'Copy Player Link'}
              </button>
              <button className="btn btn--sm btn--neutral" onClick={onBack}>Exit</button>
            </div>
          </div>
        </div>
      </div>

      <div className="container container--wide" style={{ marginTop: 24 }}>
        {/* Lobby */}
        {gameState.status === 'lobby' && (
          <div className="card text-center" style={{ padding: '40px 32px' }}>
            <div style={{ fontSize: '3rem', marginBottom: 12 }}>⏳</div>
            <div className="section-title" style={{ marginBottom: 8 }}>Waiting for participants</div>
            <p className="muted" style={{ marginBottom: 8 }}>
              Share the PIN <strong>{activePin}</strong> or the player link below.
            </p>
            <div style={{ display: 'flex', gap: 8, maxWidth: 400, margin: '0 auto 24px' }}>
              <input
                className="input"
                readOnly
                value={`${window.location.origin}${window.location.pathname}?mode=player`}
                onFocus={e => e.target.select()}
                style={{ fontSize: '0.8rem' }}
              />
              <button className="btn btn--ghost btn--sm" onClick={copyPlayerLink}>{copied ? '✓' : 'Copy'}</button>
            </div>
            <div className="flex items-center justify-center" style={{ gap: 10, marginBottom: 24 }}>
              <span className="lobby-pulse" />
              <span className="muted">{gameState.paxCount} joined so far</span>
            </div>
            <button className="btn btn--success btn--lg" onClick={advanceGame}>
              Start Game →
            </button>
          </div>
        )}

        {/* Active question */}
        {gameState.status === 'question' && currentQ && !showResults && (
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>
            {/* Left: question info */}
            <div>
              <div className="card" style={{ marginBottom: 16 }}>
                <div className="flex items-center justify-between" style={{ marginBottom: 12 }}>
                  <span className="question-item__type">{currentQ.type}</span>
                  <span className="muted" style={{ fontSize: '0.85rem' }}>
                    Q{gameState.currentQuestionIndex + 1} / {gameState.questions.length}
                  </span>
                </div>
                <div className="progress-bar" style={{ marginBottom: 16 }}>
                  <div className="progress-bar__fill" style={{ width: `${progress}%` }} />
                </div>
                <p style={{ fontSize: '1.3rem', fontWeight: 700, lineHeight: 1.3 }}>{currentQ.prompt}</p>
                {currentQ.options?.length > 0 && (
                  <div style={{ marginTop: 12, display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                    {currentQ.options.map((opt, i) => (
                      <span key={i} style={{ background: 'var(--brand-light)', color: 'var(--brand)', padding: '4px 12px', borderRadius: 999, fontSize: '0.85rem', fontWeight: 600 }}>{opt}</span>
                    ))}
                  </div>
                )}
              </div>

              <div className="card">
                <div className="flex items-center justify-between" style={{ marginBottom: 12 }}>
                  <span className="section-title" style={{ marginBottom: 0 }}>Responses</span>
                  <span className="stat-pill" style={{ background: 'var(--brand-light)', color: 'var(--brand)', fontSize: '0.85rem' }}>
                    {currentAnswers.length} / {gameState.paxCount}
                  </span>
                </div>
                <div className="progress-bar" style={{ marginBottom: 16 }}>
                  <div className="progress-bar__fill" style={{ width: gameState.paxCount > 0 ? `${(currentAnswers.length / gameState.paxCount) * 100}%` : '0%' }} />
                </div>
                <button className="btn btn--primary btn--full" onClick={() => setShowResults(true)}>
                  Show Results
                </button>
              </div>
            </div>

            {/* Right: live feed */}
            <div className="card">
              <div className="section-title" style={{ marginBottom: 12 }}>Live Feed</div>
              {currentAnswers.length === 0 ? (
                <div className="text-center" style={{ padding: '32px 0' }}>
                  <div style={{ fontSize: '2rem', marginBottom: 8 }}>⌛</div>
                  <p className="muted">Waiting for responses...</p>
                </div>
              ) : (
                <div className="response-feed">
                  {currentAnswers.map((ans, i) => (
                    <div key={i} className="response-item">
                      <span className="response-item__id">{ans.userId.substring(5, 10)}</span>
                      <span style={{ wordBreak: 'break-word' }}>
                        {ans.answer.toString().startsWith('http') ? (
                          <a href={ans.answer} target="_blank" rel="noreferrer" style={{ color: 'var(--brand)' }}>View Photo</a>
                        ) : ans.answer.toString().substring(0, 80)}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Results panel — shown after all answer or host clicks Show Results */}
        {gameState.status === 'question' && currentQ && showResults && (
          <ResultsPanel
            question={currentQ}
            answers={currentAnswers}
            onNext={advanceGame}
            isLast={gameState.currentQuestionIndex === gameState.questions.length - 1}
          />
        )}

        {/* Ended */}
        {gameState.status === 'ended' && (
          <div>
            <div className="card text-center" style={{ padding: '40px 32px', marginBottom: 20 }}>
              <div style={{ fontSize: '3rem', marginBottom: 12 }}>🎉</div>
              <div className="section-title" style={{ marginBottom: 8 }}>Session Complete</div>
              <p className="muted" style={{ marginBottom: 24 }}>
                {gameState.paxCount} participants · {gameState.questions.length} questions · {Object.values(gameState.responses || {}).reduce((s, a) => s + a.length, 0)} total responses
              </p>
              <div className="flex justify-center" style={{ gap: 12 }}>
                <button className="btn btn--success btn--lg" onClick={exportPDF}>Export PDF Report</button>
                <button className="btn btn--neutral" onClick={onBack}>Exit</button>
              </div>
            </div>

            <EndSummary gameState={gameState} />
          </div>
        )}
      </div>
    </div>
  );
}
